"use client";

import { useState, useEffect, useCallback } from "react";
import { usePathname } from "next/navigation";
import { FinWordContent, FinWordGameState, FinWordStats as StatsType, DailyChallengeState, GameStatus } from "@/lib/daily/types";
import {
  loadFinWordState,
  saveFinWordState,
  loadFinWordStats,
  saveFinWordStats,
  loadDailyChallengeState,
  saveDailyChallengeState,
} from "@/lib/daily/storageManager";
import { updateFinWordStats } from "@/lib/daily/scoreCalculator";
import { sanitizeGuess, isValidWord } from "@/lib/daily/validators/wordValidator";
import { getAbsoluteDayIndex } from "@/lib/daily/contentSelector";
import { getCalculatorUrl, extractCalculatorSlug, getActiveCountry } from "@/lib/utils/link-utils";
import FinWordGrid from "./FinWordGrid";
import FinWordKeyboard from "./FinWordKeyboard";
import FinWordStats from "./FinWordStats";
import { HelpCircle, RefreshCw, Eye, ArrowLeft } from "lucide-react";
import Link from "next/link";
import YouMightAlsoLike from "@/components/blog/YouMightAlsoLike";
import { getDynamicYMALItems, type YMALPoolItem } from "@/lib/blog/ymal-mapper";

interface FinWordGameProps {
  readonly content: FinWordContent;
  readonly date: string;
  readonly mode?: "daily" | "practice";
  // Light blog pool computed server-side (see DailyHub.ymalPool)
  readonly ymalPool?: readonly YMALPoolItem[];
}

export default function FinWordGame({ content, date, mode = "daily", ymalPool = [] }: FinWordGameProps) {
  const pathname = usePathname();
  const activeCountry = getActiveCountry(pathname);
  const solution = content.word.toUpperCase();
  const wordId = content.id;
  const dayNumber = getAbsoluteDayIndex(date) + 1;

  const [gameState, setGameState] = useState<FinWordGameState | null>(null);
  const [currentGuess, setCurrentGuess] = useState("");
  const [shakeRowIndex, setShakeRowIndex] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [stats, setStats] = useState<StatsType | null>(null);
  const [highContrast, setHighContrast] = useState(false);

  // Generate calculator link
  const calculatorLink = content.calculatorLink ? (() => {
    if (content.calculatorLink.startsWith("/compare")) {
      return content.calculatorLink; // Comparisons don't have country prefix
    }
    const slug = extractCalculatorSlug(content.calculatorLink);
    return getCalculatorUrl(slug, activeCountry);
  })() : null;

  // Load state on mount
  useEffect(() => {
    // Load high contrast settings
    const contrast = localStorage.getItem("daily_high_contrast") === "true";
    setHighContrast(contrast);

    // Load stats
    const loadedStats = loadFinWordStats();
    setStats(loadedStats);

    // Load game state
    const savedState = loadFinWordState(date);
    if (savedState && savedState.word === solution) {
      setGameState(savedState);
    } else {
      const newState: FinWordGameState = {
        date,
        word: solution,
        guesses: [],
        status: "playing",
        startTime: Date.now(),
      };
      saveFinWordState(newState);
      setGameState(newState);
    }
  }, [date, solution]);

  const triggerShake = useCallback((index: number) => {
    setShakeRowIndex(index);
    setTimeout(() => setShakeRowIndex(null), 350);
  }, []);

  const showAnnouncement = (msg: string) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(""), 3000);
  };

  const submitGuess = useCallback(
    (guess: string) => {
      if (!gameState || gameState.status !== "playing") return;

      const guessesCount = gameState.guesses.length;
      if (guessesCount >= 6) return;

      const sanitized = sanitizeGuess(guess);

      if (sanitized.length !== 5) {
        triggerShake(guessesCount);
        showAnnouncement("Not enough letters");
        return;
      }

      if (gameState.guesses.includes(sanitized)) {
        triggerShake(guessesCount);
        showAnnouncement("Already guessed");
        return;
      }

      if (!isValidWord(sanitized) && sanitized !== solution) {
        triggerShake(guessesCount);
        showAnnouncement("Not a valid word");
        return;
      }

      // Add guess
      const newGuesses = [...gameState.guesses, sanitized];
      let status: GameStatus = gameState.status;
      let endTime = gameState.endTime;

      const isWin = sanitized === solution;
      const isLoss = newGuesses.length >= 6 && !isWin;

      if (isWin) {
        status = "won";
        endTime = Date.now();
      } else if (isLoss) {
        status = "lost";
        endTime = Date.now();
      }

      const updatedState: FinWordGameState = {
        ...gameState,
        guesses: newGuesses,
        status,
        endTime,
      };

      saveFinWordState(updatedState);
      setGameState(updatedState);
      setCurrentGuess("");

      // Handle game over (update stats & overall hub streak)
      if (isWin || isLoss) {
        // Only update stats and streak in daily mode
        if (mode === "daily") {
          const hubState = loadDailyChallengeState();

          let streak = hubState.streak;
          const wasCompleted = hubState.wordCompleted;

          if (!wasCompleted) {
            if (stats) {
              const updatedStats = updateFinWordStats(stats, isWin, newGuesses.length, date);
              saveFinWordStats(updatedStats);
              setStats(updatedStats);
            }

            // Update completion
            const isAllOtherDone = hubState.quizCompleted;
            let lastCompletedDate = hubState.lastCompletedDate;
            if (isAllOtherDone) {
              streak += 1;
              lastCompletedDate = date;
            }

            const updatedHub: DailyChallengeState = {
              ...hubState,
              wordCompleted: true,
              streak,
              lastCompletedDate,
            };
            saveDailyChallengeState(updatedHub);

            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event("storage"));
            }
          }
        }
      }
    },
    [gameState, solution, stats, date, mode, triggerShake]
  );

  const handleKeyPress = useCallback(
    (key: string) => {
      if (!gameState || gameState.status !== "playing") return;

      const guessesCount = gameState.guesses.length;
      if (guessesCount >= 6) return;

      if (key === "BACKSPACE" || key === "BACK") {
        setCurrentGuess((prev) => prev.slice(0, -1));
      } else if (key === "ENTER") {
        submitGuess(currentGuess);
      } else {
        // Normal letter keys
        const sanitizedKey = key.toUpperCase().replace(/[^A-Z]/g, "");
        if (sanitizedKey && sanitizedKey.length === 1 && currentGuess.length < 5) {
          const nextGuess = currentGuess + sanitizedKey;
          setCurrentGuess(nextGuess);

          if (nextGuess.length === 5) {
            // 100ms delay so the 5th letter renders in the grid before submission flips start
            setTimeout(() => {
              submitGuess(nextGuess);
            }, 100);
          }
        }
      }
    },
    [gameState, currentGuess, submitGuess]
  );

  // Sync physical keyboard keystrokes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      
      const key = e.key.toUpperCase();
      if (key === "BACKSPACE" || key === "DELETE") {
        handleKeyPress("BACKSPACE");
      } else if (key === "ENTER") {
        handleKeyPress("ENTER");
      } else if (/^[A-Z]$/.test(key)) {
        handleKeyPress(key);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyPress]);

  const toggleHighContrast = () => {
    const nextVal = !highContrast;
    setHighContrast(nextVal);
    localStorage.setItem("daily_high_contrast", String(nextVal));
  };

  if (!gameState || !stats) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <RefreshCw size={24} className="icon-spin" />
        <p>Loading game state...</p>
      </div>
    );
  }

  const isGameOver = gameState.status !== "playing";

  return (
    <>
      <div className="back-link-wrapper" style={{ marginBottom: "0.5rem" }}>
        <Link href="/daily" className="back-link">
          <ArrowLeft size={16} /> Back to Daily Challenge Hub
        </Link>
      </div>

      <div className={`finword-game-container ${highContrast ? "high-contrast" : ""}`}>
        <div className="finword-game-layout">
          {/* Left Column: Game Board & Keyboard */}
          <div className="finword-board-column">
            {/* Practice Mode Badge */}
            {mode === "practice" && (
              <div
                style={{
                  backgroundColor: "var(--secondary-light)",
                  border: "1px solid var(--secondary-a30)",
                  borderRadius: "var(--radius-md)",
                  padding: "8px 16px",
                  marginBottom: "1rem",
                  textAlign: "center",
                  color: "var(--info)",
                  fontWeight: "var(--fw-bold)",
                  fontSize: "var(--text-sm)",
                }}
              >
                🎮 Practice Mode — Unlimited
              </div>
            )}
            
            {/* Header controls */}
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--fw-bold)", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                FinWord #{dayNumber}
              </span>
              <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setShowHint((h) => !h)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text-secondary)",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: "var(--fw-bold)",
                }}
              >
                <HelpCircle size={16} />
                <span>Clue</span>
              </button>
              <button
                onClick={toggleHighContrast}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: highContrast ? "var(--accent-orange)" : "var(--text-secondary)",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: "var(--fw-bold)",
                }}
                aria-label={`Toggle high contrast: currently ${highContrast ? "on" : "off"}`}
              >
                <Eye size={16} />
                <span>Contrast: {highContrast ? "On" : "Off"}</span>
              </button>
            </div>
          </div>

          {/* Clue box */}
          {showHint && (
            <div
              className="anim-slide-up"
              style={{
                width: "100%",
                backgroundColor: "var(--overlay-a03)",
                border: "1px solid var(--border-color)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-4)",
                textAlign: "center",
                fontSize: "var(--text-sm)",
              }}
            >
              <strong>Definition Clue:</strong> {content.hint}
            </div>
          )}

          {/* Screen reader alerts */}
          <div
            className="sr-only"
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              width: "1px",
              height: "1px",
              padding: 0,
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              border: 0,
            }}
          >
            {errorMessage}
          </div>

          {/* Custom Error Toast */}
          {errorMessage && (
            <div
              style={{
                position: "fixed",
                top: "10%",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "var(--text-primary)",
                color: "var(--bg-main)",
                padding: "8px 16px",
                borderRadius: "4px",
                fontWeight: "var(--fw-extrabold)",
                zIndex: 100,
                boxShadow: "var(--shadow-lg)",
                animation: "slideInUp 0.15s ease-out",
              }}
            >
              {errorMessage}
            </div>
          )}

          {/* Letter Grid */}
          <FinWordGrid
            guesses={gameState.guesses}
            currentGuess={currentGuess}
            solution={solution}
            isSubmitted={isGameOver}
            shakeRowIndex={shakeRowIndex}
          />

          {/* Virtual Keyboard */}
          {!isGameOver && (
            <FinWordKeyboard
              onKeyPress={handleKeyPress}
              guesses={gameState.guesses}
              solution={solution}
              disabled={isGameOver}
            />
          )}
        </div>

        {/* Right Column: Definition Box + Statistics */}
        <div className={`finword-stats-column ${!isGameOver ? "during-play" : ""}`}>
          {/* Definition revealed on Win/Loss */}
          {isGameOver && (
            <div className="finword-definition-box anim-slide-up" style={{ width: "100%", marginBottom: "0.6rem", padding: "0.6rem 0.85rem" }}>
              <div className="finword-def-title" style={{ fontSize: "var(--text-sm)", marginBottom: "4px" }}>
                {solution} ({content.category.toUpperCase()})
              </div>
              <div className="finword-def-text" style={{ fontSize: "var(--text-xs)", lineHeight: 1.35, marginBottom: "4px" }}>{content.definition}</div>
              <div className="finword-def-example" style={{ fontSize: "var(--text-xs)" }}>
                <strong>Example:</strong> {content.example}
              </div>
              {calculatorLink && (
                <Link
                  href={calculatorLink}
                  style={{
                    display: "inline-block",
                    marginTop: "6px",
                    color: "var(--primary)",
                    fontWeight: "var(--fw-bold)",
                    fontSize: "var(--text-xs)",
                    textDecoration: "underline",
                  }}
                >
                  {calculatorLink.startsWith("/compare") ? "Open Related Comparison" : "Open Related Calculator"} &rarr;
                </Link>
              )}
            </div>
          )}

          <FinWordStats
            stats={stats}
            dayNumber={dayNumber}
            guesses={gameState.guesses}
            solution={solution}
            won={gameState.status === "won"}
            isGameOver={isGameOver}
            startTime={gameState.startTime}
            endTime={gameState.endTime}
            mode={mode}
          />
        </div>
      </div>

      <div className="section-divider"></div>
      <YouMightAlsoLike noMargin={true} items={getDynamicYMALItems([...ymalPool], "finword-" + date)} />
    </div>
    </>
  );
}
